import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import {
  apiRequest,
  ApiError,
  HttpMethod,
  isSuccessStatus,
} from "../lib/api-request";

// Per-call values passed to `mutate` / `mutateAsync`. The endpoint + method
// come from the hook args; variables carry the body and any param/path
// overrides for this specific call.
export interface ApiMutationVariables {
  data?: any;
  params?: Record<string, any>;
  pathParam?: string;
  headers?: Record<string, any>;
}

type UseApiMutationArgs<TData, TVars> = {
  endpoint: string;
  method?: HttpMethod;
  headers?: Record<string, any>;
} & Omit<UseMutationOptions<TData, ApiError, TVars>, "mutationFn">;

export function useApiMutation<
  TData = any,
  TVars extends ApiMutationVariables = ApiMutationVariables,
>(args: UseApiMutationArgs<TData, TVars>) {
  const { endpoint, method = "POST", headers, ...mutationOptions } = args;

  return useMutation<TData, ApiError, TVars>({
    ...mutationOptions,
    mutationFn: async (vars) => {
      const { body, status } = await apiRequest<TData>({
        endpoint,
        method,
        data: vars?.data,
        params: vars?.params,
        pathParam: vars?.pathParam,
        headers: { ...headers, ...vars?.headers },
      });
      if (!isSuccessStatus(status)) throw new ApiError(status, body);
      return body;
    },
  });
}
